import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Field, inputCls, Btn, AdminImageUpload } from "./kit";

const GROUPS = [
  { title: "Company Identity", fields: [["company_name", "Company Name"], ["descriptor", "Descriptor"], ["tagline", "Tagline"], ["established_year", "Established Year"]] },
  { title: "Contact", fields: [["email", "Email"], ["rfq_notification_email", "RFQ Alert Email (receives new quote requests)"], ["phone", "Phone"], ["whatsapp", "WhatsApp (digits only, incl. country code)"], ["address", "Address"], ["warehouse_address", "Warehouse Address"], ["business_hours", "Business Hours"]] },
  { title: "Social Media", fields: [["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["linkedin", "LinkedIn URL"]] },
  { title: "Logistics Defaults", fields: [["port_of_loading", "Port of Loading"], ["packaging_options", "Packaging Options"], ["bag_size", "Bag Size"], ["container_options", "Container Options"], ["moq", "MOQ"], ["lead_time", "Lead Time"], ["incoterms", "Incoterms"], ["payment_terms", "Payment Terms"]] },
];

export default function AdminCompany() {
  const { reload } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => { try { const r = await api.get("/company"); setForm(r.data); } catch (e) {} })(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, low_stock_threshold: parseFloat(form.low_stock_threshold) || 0 };
      await api.put("/admin/company", payload);
      toast.success("Company settings saved");
      reload();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!form) return <div className="font-mono text-sm text-offwhite/50">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-extrabold text-offwhite">Company Settings</h1><p className="mt-1 font-body text-sm text-offwhite/55">Edit business information shown across the website.</p></div>
        <Btn onClick={save} disabled={saving} data-testid="save-company-btn"><Save className="mr-1 inline h-4 w-4" /> {saving ? "Saving…" : "Save"}</Btn>
      </div>

      <div className="mb-6 rounded-xl border border-offwhite/10 bg-[#101c14] p-6">
        <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Branding</h3>
        <div className="flex flex-wrap gap-8">
          <AdminImageUpload label="Logo" value={form.logo} onChange={(v) => set("logo", v)} testId="company-logo-upload" />
          <AdminImageUpload label="Favicon" value={form.favicon} onChange={(v) => set("favicon", v)} />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AdminImageUpload label="Company Profile (PDF as file)" value={form.company_profile_pdf} onChange={(v) => set("company_profile_pdf", v)} />
          <AdminImageUpload label="Product Catalog (PDF as file)" value={form.catalog_pdf} onChange={(v) => set("catalog_pdf", v)} />
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-offwhite/10 bg-[#101c14] p-6">
        <h3 className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Homepage Feature Story</h3>
        <p className="mb-4 font-body text-xs text-offwhite/50">Choose your strongest photo to feature large behind the homepage "See Where Your Coffee Comes From" section. Leave empty to use the default warehouse photo.</p>
        <div className="flex flex-wrap items-start gap-8">
          <AdminImageUpload label="Feature Image" value={form.home_feature_image} onChange={(v) => set("home_feature_image", v)} testId="company-home-feature-upload" />
          <div className="min-w-[240px] flex-1">
            <Field label="Feature Caption (optional)"><input data-testid="company-home-feature-caption" className={inputCls} value={form.home_feature_caption || ""} onChange={(e) => set("home_feature_caption", e.target.value)} placeholder="e.g. Our warehouse in North Sumatra" /></Field>
          </div>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="mb-6 rounded-xl border border-offwhite/10 bg-[#101c14] p-6">
          <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">{g.title}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {g.fields.map(([k, lbl]) => (
              <Field key={k} label={lbl}><input data-testid={`company-${k}`} className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>
            ))}
          </div>
        </div>
      ))}

      <div className="mb-6 rounded-xl border border-offwhite/10 bg-[#101c14] p-6">
        <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Company Description</h3>
        <textarea rows={4} className={inputCls} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
      </div>

      <div className="mb-6 rounded-xl border border-offwhite/10 bg-[#101c14] p-6">
        <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Inventory Settings</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Default Low Stock Threshold"><input type="number" data-testid="company-threshold" className={inputCls} value={form.low_stock_threshold ?? 10} onChange={(e) => set("low_stock_threshold", e.target.value)} /></Field>
          <label className="flex items-end gap-2 pb-2 font-body text-sm text-offwhite/80"><input type="checkbox" checked={!!form.show_low_stock_public} onChange={(e) => set("show_low_stock_public", e.target.checked)} className="h-4 w-4 accent-forest" /> Show low-stock warnings publicly</label>
        </div>
      </div>

      <Btn onClick={save} disabled={saving} data-testid="save-company-btn-bottom"><Save className="mr-1 inline h-4 w-4" /> {saving ? "Saving…" : "Save Settings"}</Btn>
    </div>
  );
}
